import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import { type ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideServiceWorker } from '@angular/service-worker';

import { provideCore } from '@core/core.public-api';

import { environment } from '@env/environment';
import { routes } from './app.routes';

// Locale es-AR para TODOS los pipes de i18n (date/number/currency/percent):
// fechas dd/mm/yyyy y reloj 24h app-wide, en lugar del default en-US (12h AM/PM).
registerLocaleData(localeEsAr);

/**
 * Composicion raiz de providers.
 *
 * Los singletons transversales (config runtime, http+interceptores, OIDC, observabilidad,
 * error handling) se cablean en `provideCore()` (core/core.providers.ts), que ademas fija el
 * ORDEN de los APP_INITIALIZER: config -> http -> auth -> observability -> error handler.
 * `TenantContextService`, `PermissionService` y `RealtimeConnectionService` son
 * `providedIn: 'root'`, por eso no aparecen aca. app.config solo suma lo propio del framework:
 * router, animaciones y PWA.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    // eventCoalescing reduce ciclos de CD bajo la avalancha de eventos de telemetria/WS.
    // (El design doc evalua zoneless a futuro; se migra cambiando solo esta linea.)
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Formato regional argentino para pipes de fecha/numero (registrado arriba).
    { provide: LOCALE_ID, useValue: 'es-AR' },

    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),

    provideAnimationsAsync(),

    // Singletons de core (orden de inicializadores definido adentro).
    provideCore(),

    // PWA: solo en prod, registra cuando la app queda estable (no compite con el arranque).
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
